/**
 * Derives the issued-payment timeline for the Portal Payments component from
 * the payment fields RC syncs back to applicantinfo. Shared by the portal
 * frontend and backend so the semantics stay consistent.
 *
 * Only payments that have actually been issued are shown — in-flight and
 * failed payments are deliberately invisible here; programs alert applicants
 * about those through other channels.
 */

export type AppInfo = Record<string, string | undefined>;

export type PortalIssuedPayment = {
  targetField: string,
  /** Vendor confirmation (Dwolla transfer id, Zelle payment instruction id,
   *  USIO load confirmation, GiveCard vendor id) — what the applicant quotes
   *  to the payment provider's support. */
  reference: string,
  issuedDate?: string,
  amountInCents?: number,
  amountFormatted?: string,
  paymentType?: string,
  /** ACH trace number, once Dwolla has assigned one — Dwolla ACH only for now. */
  traceId?: string,
};

/**
 * Returns the payment's issued state, or null when it has not been issued
 * (not started, still in flight, failed, or cancelled — none of which this
 * component shows).
 *
 * Issued fields land via the payment_issued RC event
 * (lib/payments/events/shared_events.ts in the aidkit package).
 */
export function deriveIssuedPayment(info: AppInfo, targetField: string): PortalIssuedPayment | null {
  const v = (suffix: string) => info[targetField + suffix];

  const reference = v('_issued');
  if (!reference) return null;

  const amountInCents = parseInt(v('_amount_in_cents') || '', 10);
  return {
    targetField,
    reference,
    issuedDate: v('_issued_date'),
    amountInCents: Number.isFinite(amountInCents) ? amountInCents : undefined,
    amountFormatted: v('_amount_formatted'),
    paymentType: v('_payment_type'),
    traceId: v('_trace_id'),
  };
}

/** Sum of issued amounts, for the "total amount paid" summary. */
export function totalPaidInCents(payments: PortalIssuedPayment[]): number {
  return payments.reduce((sum, p) => sum + (p.amountInCents || 0), 0);
}

/** The applicantinfo suffixes the Portal Payments component reads per payment. */
export const PORTAL_PAYMENT_FIELD_SUFFIXES = [
  '_issued',
  '_issued_date',
  '_amount_in_cents',
  '_amount_formatted',
  '_payment_type',
  '_trace_id',
] as const;

/**
 * Applicant-level (not per-payment) fields the component reads:
 * applicant_payment_type backs the "current payment method" summary.
 */
export const PORTAL_PAYMENT_APPLICANT_FIELDS = ['applicant_payment_type'] as const;
